import React from 'react';
import styled from 'styled-components';

import { isDefined } from '../../../utils/variables';
import { SkyView } from '../../../viewer/skyview';
import state from '../../../viewer/state';
import { Point2d } from '../../../viewer/utilities/math-helpers';
import { viewerZIndices } from '../../../viewer/viewer-z-indices';
import { DesignLicenseDialog } from '../../components/design-license-dialog/design-license-dialog';
import { SkyViewContextMenu, SkyViewContextMenuProps } from '../../components/skyview/context-menu';
import { SkyViewLoader } from '../../components/skyview/skyview-loader';
import {
  SkyviewAssetPositionProps,
  SkyviewAssetPositionTooltip,
} from '../../components/skyview/tooltip/skyview-asset-position-tooltip';
import { VolumeReportCanvas } from '../../components/skyview-new/components/report-tool-menu/volume-overview-pdf-report/volume-report-canvas';
import { SaveGeometryAssetDialog } from '../../components/skyview-new/components/save-geometry-asset-dialog';
import { SetAssetCoordinate } from '../../components/skyview-new/components/set-asset-coordinate/set-asset-coordinate';
import { Skyview } from '../../components/skyview-new/skyview';

type Props = {
  skyView: SkyView;
  isNativeApp: boolean;
  assetMenuVisible?: boolean;
  assetTooltip?: SkyviewAssetPositionProps;
  contextMenu?: SkyViewContextMenuProps;
};

// SkyviewFrameContent is the only component that subscribes to the viewer's
// animation loop (the updateUI event). It re-renders at the viewer's render rate
// (~30 fps) to re-read imperative skyView methods whose return values can change
// every frame (e.g. isLoading, getActiveScene, updatedScreenRatio).
//
// Keeping this subscription in a dedicated leaf component means the rest of the
// tree — SkyviewPage, Skyview, AssetMenu, and all asset rows — is never triggered
// by the animation loop. Those components only re-render from actual React state
// changes (scene transitions, tool switches, tooltip events).
//
// Skyview is wrapped in React.memo. Because showSpinner and assetMenuVisible only
// change at scene boundaries, React.memo skips the re-render on every ordinary
// frame tick. skyviewChildren is memoized to keep the children prop reference
// stable so it does not defeat React.memo.
const SkyviewFrameContent = ({
  skyView,
  isNativeApp,
  assetMenuVisible,
  assetTooltip,
  contextMenu,
}: Props) => {
  const [, setTick] = React.useState(0);
  const tickScheduledRef = React.useRef(false);

  React.useEffect(() => {
    let frameHandle: number | undefined;

    const onUpdateUI = () => {
      if (tickScheduledRef.current) {
        return;
      }
      tickScheduledRef.current = true;

      frameHandle = requestAnimationFrame(() => {
        frameHandle = undefined;
        setTick((value) => value + 1);
        // Allow next updateUI to schedule only after this update has been committed,
        // so a synchronous updateUI during re-render does not cause another setState.
        requestAnimationFrame(() => {
          tickScheduledRef.current = false;
        });
      });
    };

    skyView.addEventListener('updateUI', onUpdateUI);

    return () => {
      if (isDefined(frameHandle)) {
        cancelAnimationFrame(frameHandle);
      }
      tickScheduledRef.current = false;
      skyView.removeEventListener('updateUI', onUpdateUI);
    };
  }, [skyView]);

  // Until SkyView's scene has opened. The timeline data then loads with the viewer shown.
  const showLoader = !skyView.getActiveScene();
  const showSpinner = Boolean(skyView.isLoading() && !isNativeApp);
  const showViewer = Boolean(skyView.getActiveScene() && !isNativeApp);

  const gpsIconVisible = Boolean(state.getValue('gpsIconVisible'));
  const gpsMarker: Point2d | undefined =
    skyView.isOrthoViewActive() && gpsIconVisible ? skyView.updatedScreenRatio : undefined;

  const SetAssetCoordinateComponent = SetAssetCoordinate as React.ComponentType;
  const SaveGeometryAssetDialogComponent = SaveGeometryAssetDialog as React.ComponentType;
  const DesignLicenseDialogComponent = DesignLicenseDialog as React.ComponentType;
  const VolumeReportCanvasComponent = VolumeReportCanvas as React.ComponentType;

  const skyviewChildren = React.useMemo(
    () => (
      <>
        <SetAssetCoordinateComponent />
        <SaveGeometryAssetDialogComponent />
        <DesignLicenseDialogComponent />
        <VolumeReportCanvasComponent />
      </>
    ),
    // These component references are stable cast aliases — no deps needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <>
      {showLoader && <SkyViewLoader />}

      {showViewer && gpsMarker && (
        <GpsMarker style={{ top: `${gpsMarker.y}%`, left: `${gpsMarker.x}%` }}>
          <div className="fas fa-map-marker gps-marker" />
        </GpsMarker>
      )}

      {showViewer && assetTooltip && (
        <SkyviewAssetPositionTooltip
          coordinate={assetTooltip.coordinate}
          coordinateSystem={assetTooltip.coordinateSystem}
          heights={assetTooltip.heights}
          properties={assetTooltip.properties}
          screenPoint={assetTooltip.screenPoint}
        />
      )}

      {showViewer && contextMenu && (
        <SkyViewContextMenu items={contextMenu.items} pos={contextMenu.pos} />
      )}

      {showViewer && (
        <Skyview assetMenuVisible={assetMenuVisible} showSpinner={showSpinner} skyView={skyView}>
          {skyviewChildren}
        </Skyview>
      )}
    </>
  );
};

export { SkyviewFrameContent };

const GpsMarker = styled.div`
  position: absolute;
  z-index: ${viewerZIndices.menu};
`;
